import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Layout from "../../components/global/layout";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import swal from "sweetalert";
import { toast } from "react-toastify";
import ActionMenu from "../../components/common/ActionMenu";
import CommonPagination from "../../components/common/CommonPagination";
import FormControl from "../../components/common/FormControl";
import { MODULE_CONFIG } from "./shared";
import { capitalize } from "../../methods/methods";
import {
  FiPlus,
  FiSearch,
  FiEye,
  FiEdit3,
  FiTrash2,
  FiRotateCcw,
  FiX,
  FiCheckCircle,
  FiXCircle,
  FiLayers,
} from "react-icons/fi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { hasPermission } from "../../utils/permission";

export default function PlansList() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const canCreate = hasPermission(user, "plans", "create");
  const canEdit = hasPermission(user, "plans", "edit");
  const canDelete = hasPermission(user, "plans", "delete");

  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Filters & Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Handle immediate search input clearing
  const handleSearchChange = (val) => {
    setSearchQuery(val);
    if (!val || !val.trim()) {
      loader(true);
      setDebouncedSearch("");
      setPage(1);
    }
  };

  // Debounce search input (350ms)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setDebouncedSearch("");
      return;
    }
    const handler = setTimeout(() => {
      loader(true);
      setDebouncedSearch(searchQuery.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Fetch plans from API with platform loader
  const fetchPlans = useCallback(
    async (showLoader = true) => {
      if (showLoader) loader(true);
      setLoading(true);

      try {
        const queryParams = new URLSearchParams();
        queryParams.append("page", String(page));
        // Always send status parameter; send status=all when all or empty
        queryParams.append(
          "status",
          statusFilter && statusFilter.trim() !== "" ? statusFilter : "all"
        );

        if (debouncedSearch && debouncedSearch.trim()) {
          queryParams.append("search", debouncedSearch.trim());
        }

        const endpoint = `${MODULE_CONFIG.endpoints.listing}?${queryParams.toString()}`;
        const res = await ApiClient.get(endpoint);

        if (res?.success) {
          const data = res?.data;
          const records = Array.isArray(data?.plans)
            ? data.plans
            : Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
            ? data.data
            : [];
          setItems(records);

          const dynamicTotal =
            res?.total ??
            res?.totalDocs ??
            data?.total ??
            data?.totalDocs ??
            records.length;
          setTotalCount(Number(dynamicTotal) || 0);
        }
      } catch (err) {
        console.error("Failed to fetch subscription plans:", err);
      } finally {
        setLoading(false);
        if (showLoader) loader(false);
      }
    },
    [page, limit, statusFilter, debouncedSearch]
  );

  useEffect(() => {
    fetchPlans(true);
  }, [fetchPlans]);

  // Reset filters
  const isFilterActive = Boolean(
    searchQuery.trim() || (statusFilter && statusFilter !== "all")
  );

  const handleResetFilters = () => {
    loader(true);
    setSearchQuery("");
    setDebouncedSearch("");
    setStatusFilter("all");
    setPage(1);
  };

  // Status Toggle (Active / Deactive)
  const handleToggleStatus = (item) => {
    const id = item?.id || item?._id;
    const isCurrentlyActive = item?.status === "active";
    const nextStatus = isCurrentlyActive ? "deactive" : "active";

    swal({
      title: `${isCurrentlyActive ? "Deactivate" : "Activate"} Plan?`,
      text: `Are you sure you want to mark "${item?.name}" as ${nextStatus}?`,
      icon: "warning",
      buttons: ["Cancel", "Yes, Proceed"],
      dangerMode: isCurrentlyActive,
      className: "smallmodal",
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      try {
        const res = await ApiClient.put(MODULE_CONFIG.endpoints.statusChange, {
          id: id,
          status: nextStatus,
        });
        if (res?.success) {
          toast.success(`Plan marked as ${capitalize(nextStatus)}.`);
          fetchPlans(true);
        }
      } catch (err) {
        console.error("Failed to toggle plan status:", err);
      } finally {
        loader(false);
      }
    });
  };

  // Delete Plan
  const handleDelete = (item) => {
    const id = item?.id || item?._id;
    swal({
      title: "Delete Subscription Plan?",
      text: `Are you sure you want to delete "${item?.name}"? This action cannot be undone.`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
      className: "smallmodal",
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      try {
        const res = await ApiClient.delete(
          `${MODULE_CONFIG.endpoints.delete}?id=${id}`
        );
        if (res?.success) {
          toast.success("Subscription plan deleted successfully.");
          fetchPlans(true);
        }
      } catch (err) {
        console.error("Failed to delete plan:", err);
      } finally {
        loader(false);
      }
    });
  };

  return (
    <Layout>
      <div className="w-full max-w-full space-y-5 pb-12 antialiased">
        {/* ======================================================== */}
        {/* 1. COMBINED MODULE TITLE & FILTER BAR (SPACE OPTIMIZED) */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="space-y-0.5 min-w-0">
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight truncate">
              {MODULE_CONFIG.title}
            </h1>
            <p className="text-xs text-slate-600 font-medium">
              {MODULE_CONFIG.subtitle}
            </p>
          </div>

          {/* Filter & Action Controls */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 flex-wrap shrink-0">
            {/* Search Input */}
            <div className="w-full sm:w-60 md:w-64">
              <div className="relative flex items-center group">
                <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400 group-focus-within:text-[#115B9D] transition-colors">
                  <FiSearch className="w-4 h-4" />
                </span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search plan name, slug..."
                  className="w-full bg-white border-[1.5px] border-slate-500 rounded-xl pl-10 pr-9 h-10 text-xs sm:text-sm font-semibold text-slate-900 placeholder:text-slate-500 outline-none focus:outline-none focus:ring-0 focus:border-slate-500 shadow-box"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => handleSearchChange("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                    title="Clear search"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Status Filter Dropdown */}
            <div className="w-full sm:w-48 shrink-0">
              <FormControl
                type="select"
                placeholder="Filter by Status"
                value={statusFilter}
                theme="search"
                options={[
                  { id: "all", name: "All Statuses" },
                  { id: "active", name: "Active" },
                  { id: "deactive", name: "Deactive" },
                ]}
                onChange={(val) => {
                  loader(true);
                  setStatusFilter(val || "all");
                  setPage(1);
                }}
              />
            </div>

            {/* Reset Filters Button */}
            <button
              type="button"
              onClick={handleResetFilters}
              disabled={!isFilterActive}
              className={`inline-flex items-center justify-center gap-1.5 px-3.5 h-10 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                isFilterActive
                  ? "bg-white hover:bg-slate-100 active:bg-slate-200 text-slate-700 hover:text-slate-950 border border-slate-300 hover:border-slate-400 shadow-xs"
                  : "bg-slate-50 text-slate-400 border border-slate-200 opacity-50 cursor-not-allowed"
              }`}
              title="Reset all search and status filters"
            >
              <FiRotateCcw className={`w-3.5 h-3.5 ${isFilterActive ? "text-slate-600" : "text-slate-400"}`} />
              <span>Reset</span>
            </button>

            {/* Create Plan Button (Protected by RBAC) */}
            {canCreate && (
              <button
                type="button"
                onClick={() => navigate(`${MODULE_CONFIG.routeBase}/add`)}
                className="inline-flex items-center justify-center gap-1.5 px-4 h-10 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-sm shadow-[#115B9D]/30 cursor-pointer whitespace-nowrap active:scale-[0.98]"
              >
                <FiPlus className="w-4 h-4" />
                <span>Add Plan</span>
              </button>
            )}
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. PLANS LISTING CONTAINER (SEAMLESS CARD VIEW ON MOBILE) */}
        {/* ======================================================== */}
        <div className="bg-transparent md:bg-white md:rounded-2xl md:border md:border-slate-200 md:shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
          {/* Loading Indicator */}
          {loading && items.length === 0 ? (
            <div className="py-16 text-center bg-white rounded-2xl border border-slate-200 md:border-0">
              <div className="w-8 h-8 border-3 border-[#115B9D] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-xs font-bold text-slate-800">Loading Plans...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center space-y-2 p-6">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 border border-slate-200 text-slate-500 flex items-center justify-center mx-auto mb-2">
                <FiLayers className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-black text-slate-900">No Plans Found</h3>
              <p className="text-xs font-medium text-slate-600 max-w-sm mx-auto">
                {debouncedSearch || statusFilter !== "all"
                  ? "No subscription plans match your current search criteria. Try resetting filters."
                  : "No subscription plans have been configured yet. Click 'Add Plan' to create one."}
              </p>
              <div className="pt-2 flex items-center justify-center gap-2">
                {isFilterActive && (
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all cursor-pointer"
                  >
                    <FiRotateCcw className="w-3.5 h-3.5" />
                    <span>Clear Filters</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate(`${MODULE_CONFIG.routeBase}/add`)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all cursor-pointer"
                >
                  <FiPlus className="w-3.5 h-3.5" />
                  <span>Create First Plan</span>
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* DESKTOP TABLE VIEW */}
              <div className="hidden md:block w-full">
                <table className="w-full table-fixed border-collapse text-left text-xs">
                  <colgroup>
                    <col style={{ width: "7%" }} />   {/* 1. Action */}
                    <col style={{ width: "22%" }} />  {/* 2. Plan Name */}
                    <col style={{ width: "17%" }} />  {/* 3. Price & Interval */}
                    <col style={{ width: "18%" }} />  {/* 4. QuickBooks Integration */}
                    <col style={{ width: "20%" }} />  {/* 5. Limits & Quotas */}
                    <col style={{ width: "16%" }} />  {/* 6. Status */}
                  </colgroup>

                  <thead className="sticky top-[72px] z-30 bg-slate-50 shadow-xs">
                    <tr className="border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider text-[11px] bg-slate-50">
                      <th className="sticky top-[72px] z-30 bg-slate-50 py-3.5 px-3 text-center whitespace-nowrap border-b border-slate-200 shadow-xs rounded-tl-2xl">
                        Action
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-50 py-3.5 px-4 text-left border-b border-slate-200 shadow-xs">
                        Plan Name
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-50 py-3.5 px-4 text-left border-b border-slate-200 shadow-xs">
                        Price & Billing
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-50 py-3.5 px-4 text-left border-b border-slate-200 shadow-xs">
                        Integration
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-50 py-3.5 px-4 text-left border-b border-slate-200 shadow-xs">
                        Quotas & Limits
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-50 py-3.5 px-4 text-left whitespace-nowrap border-b border-slate-200 shadow-xs rounded-tr-2xl">
                        Status
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {items.map((item, index) => {
                      const id = item.id || item._id;
                      const isLastRow = index === items.length - 1;
                      const hasPagination = totalCount > limit;
                      const statusInfo =
                        MODULE_CONFIG.statusMap[item.status] ||
                        MODULE_CONFIG.statusMap.active;
                      const integrationInfo =
                        MODULE_CONFIG.integrationMap[item.quickbooks_integration] ||
                        MODULE_CONFIG.integrationMap.NONE;
                      const isFree = item.type === "free" || Number(item.price) === 0;

                      return (
                        <tr
                          key={id}
                          onClick={() =>
                            navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`)
                          }
                          className="cursor-pointer transition-colors border-b border-slate-100 last:border-0 hover:bg-slate-50/90 group"
                        >
                          {/* 1. Action 3-Dots Menu (Column 1) */}
                          <td
                            className={`py-3.5 px-3 align-middle text-center ${
                              isLastRow && !hasPagination ? "rounded-bl-2xl" : ""
                            }`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-center">
                              <ActionMenu
                                align="left"
                                direction="auto"
                                actions={[
                                  {
                                    label: "View Detail",
                                    icon: <FiEye />,
                                    onClick: () =>
                                      navigate(
                                        `${MODULE_CONFIG.routeBase}/detail/${id}`
                                      ),
                                  },
                                  canEdit
                                    ? {
                                        label: "Edit Plan",
                                        icon: <FiEdit3 />,
                                        onClick: () =>
                                          navigate(
                                            `${MODULE_CONFIG.routeBase}/edit/${id}`
                                          ),
                                      }
                                    : null,
                                  canDelete
                                    ? {
                                        label: "Delete Plan",
                                        icon: <FiTrash2 />,
                                        isDanger: true,
                                        onClick: () => handleDelete(item),
                                      }
                                    : null,
                                ].filter(Boolean)}
                              />
                            </div>
                          </td>

                          {/* 2. Plan Name & Description */}
                          <td className="py-3.5 px-4 align-middle overflow-hidden">
                            <div className="min-w-0">
                              <h4 className="font-extrabold text-xs text-slate-900 group-hover:text-[#115B9D] transition-colors truncate">
                                {item.name}
                              </h4>
                              <p className="text-[11px] text-slate-500 font-medium truncate mt-0.5">
                                {item.description || item.slug || "No description provided"}
                              </p>
                            </div>
                          </td>

                          {/* 3. Price & Interval */}
                          <td className="py-3.5 px-4 align-middle whitespace-nowrap">
                            <div className="min-w-0">
                              <div className="font-black text-sm text-slate-900 flex items-baseline gap-0.5">
                                {isFree ? (
                                  <span className="text-emerald-700">Free</span>
                                ) : (
                                  <span>${item.price}</span>
                                )}
                              </div>
                              {Number(item.freeDays) > 0 ? (
                                <p className="text-[10px] font-bold text-slate-600 mt-0.5">
                                  {item.freeDays}-Day Free Trial
                                </p>
                              ) : null}
                            </div>
                          </td>

                          {/* 4. QuickBooks Integration */}
                          <td className="py-3.5 px-4 align-middle whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold border ${integrationInfo.badgeClass}`}
                            >
                              <HiOutlineBuildingOffice2 className="w-3.5 h-3.5 shrink-0" />
                              <span>{integrationInfo.shortLabel}</span>
                            </span>
                          </td>

                          {/* 5. Limits & Quotas (Dynamic API Data Only) */}
                          <td className="py-3.5 px-4 align-middle">
                            <div className="space-y-0.5 text-[11px]">
                              {item.limits?.max_users !== undefined ? (
                                <p className="text-slate-900 font-bold truncate">
                                  {item.limits.max_users === -1
                                    ? "Unlimited Users"
                                    : `${item.limits.max_users} Users`}
                                </p>
                              ) : null}
                              {item.limits?.max_monthly_quotes !== undefined ? (
                                <p className="text-slate-700 font-semibold truncate text-[10px]">
                                  {item.limits.max_monthly_quotes === -1
                                    ? "Unlimited Quotes"
                                    : `${item.limits.max_monthly_quotes} Quotes/mo`}
                                </p>
                              ) : null}
                              {item.limits?.max_users === undefined &&
                              item.limits?.max_monthly_quotes === undefined ? (
                                <span className="text-slate-500 font-medium">—</span>
                              ) : null}
                            </div>
                          </td>

                          {/* 6. Status Badge (Clickable to Toggle Status) */}
                          <td
                            className={`py-3.5 px-4 align-middle ${
                              isLastRow && !hasPagination ? "rounded-br-2xl" : ""
                            }`}
                          >
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleStatus(item);
                              }}
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wide border whitespace-nowrap cursor-pointer hover:shadow-xs hover:scale-105 active:scale-95 transition-all ${statusInfo.bgClass}`}
                              title={
                                item.status === "active"
                                  ? "Click to deactivate plan"
                                  : "Click to activate plan"
                              }
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${statusInfo.dotClass}`}
                              />
                              {statusInfo.label}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* MOBILE CARD VIEW */}
              <div className="block md:hidden space-y-3">
                {items.map((item) => {
                  const id = item.id || item._id;
                  const statusInfo =
                    MODULE_CONFIG.statusMap[item.status] ||
                    MODULE_CONFIG.statusMap.active;
                  const integrationInfo =
                    MODULE_CONFIG.integrationMap[item.quickbooks_integration] ||
                    MODULE_CONFIG.integrationMap.NONE;
                  const isFree = item.type === "free" || Number(item.price) === 0;

                  return (
                    <div
                      key={id}
                      onClick={() =>
                        navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`)
                      }
                      className="p-4 rounded-2xl border border-slate-200 hover:border-blue-300 bg-white transition-all cursor-pointer shadow-xs space-y-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <h4 className="font-extrabold text-sm text-slate-900">
                            {item.name}
                          </h4>
                          <span className="text-xs font-black text-slate-900">
                            {isFree ? "Free" : `$${item.price}`}
                          </span>
                        </div>

                        <div onClick={(e) => e.stopPropagation()}>
                          <ActionMenu
                            align="right"
                            direction="auto"
                            actions={[
                              {
                                label: "View Detail",
                                icon: <FiEye />,
                                onClick: () =>
                                  navigate(
                                    `${MODULE_CONFIG.routeBase}/detail/${id}`
                                  ),
                              },
                              canEdit
                                ? {
                                    label: "Edit Plan",
                                    icon: <FiEdit3 />,
                                    onClick: () =>
                                      navigate(
                                        `${MODULE_CONFIG.routeBase}/edit/${id}`
                                      ),
                                  }
                                : null,
                              canDelete
                                ? {
                                    label: "Delete",
                                    icon: <FiTrash2 />,
                                    isDanger: true,
                                    onClick: () => handleDelete(item),
                                  }
                                : null,
                            ].filter(Boolean)}
                          />
                        </div>
                      </div>

                      <p className="text-xs text-slate-600 line-clamp-2">
                        {item.description}
                      </p>

                      <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-[10px] font-bold border ${integrationInfo.badgeClass}`}
                        >
                          {integrationInfo.label}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleStatus(item);
                          }}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide border cursor-pointer hover:scale-105 active:scale-95 transition-all ${statusInfo.bgClass}`}
                          title={
                            item.status === "active"
                              ? "Click to deactivate"
                              : "Click to activate"
                          }
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${statusInfo.dotClass}`}
                          />
                          {statusInfo.label}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* COMMON REUSABLE PAGINATION (Hidden when total <= limit) */}
              {totalCount > limit && (
                <div className="p-4 bg-white border-t border-slate-100 rounded-b-2xl">
                  <CommonPagination
                    page={page}
                    limit={limit}
                    total={totalCount}
                    onPageChange={(newPage) => {
                      loader(true);
                      setPage(newPage);
                    }}
                    onLimitChange={(newLimit) => {
                      loader(true);
                      setLimit(newLimit);
                      setPage(1);
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
